import { ExternalLink } from 'lucide-react';
import { useState } from 'react';

import { Button } from '@/components/button';
import { Heading } from '@/components/heading';
import { ModeIcon } from '@/components/mode-icon';
import { EmissionsBadge } from '@/components/modules/emissions/badge';
import { Text } from '@/components/text';
import { SimpleDateRange } from '@/components/ui/inputs/advanced-date-picker/utils';
import { useWatchQueryParams } from '@/hooks/use-watch-query-params';
import { RouteSummaryModel } from '@/lib/models/route-summary/types';
import { formatDuration, formatFrequency } from '@/utils/format';
import { getPreferredIdentifier } from '@/utils/helpers';
import { QUERY_PARAM } from '@/utils/helpers/api-urls/constants';
import { createScheduleSearchPageUrl } from '@/utils/helpers/page-urls';
import { tv } from '@/utils/styles';
import { trackUser } from '@/utils/tracking';

export interface SchedulesAlternativeRouteCardProps {
  route: RouteSummaryModel;
  layout?: 'default' | 'row';
  showScheduleLink?: boolean;
  customTitle?: string;
}

const SchedulesAlternativeRouteCard = ({
  route,
  layout = 'default',
  showScheduleLink = true,
  customTitle,
}: SchedulesAlternativeRouteCardProps) => {
  const { origin, destination, routeSummary } = route;
  const originIdentifier = getPreferredIdentifier(origin.identifiers);
  const destinationIdentifier = getPreferredIdentifier(destination.identifiers);
  const [dateRange, setDateRange] = useState<SimpleDateRange>();

  const handleDateRangeUpdate = ({ dateFrom, dateTo }: { dateFrom?: string; dateTo?: string }) => {
    setDateRange({
      dateFrom: dateFrom ? new Date(dateFrom) : undefined,
      dateTo: dateTo ? new Date(dateTo) : undefined,
    });
  };

  useWatchQueryParams({
    paramsToWatch: [QUERY_PARAM.DATE_FROM, QUERY_PARAM.DATE_TO],
    onParamsDidUpdate: handleDateRangeUpdate,
  });

  const handleSearch = () => {
    trackUser.event('Schedules No Results: Searched Alternative Route', {
      origin: originIdentifier?.value,
      destination: destinationIdentifier?.value,
    });
  };

  const handleSeeAlternative = () => {
    trackUser.event('Schedules Sidebar: Searched Alternative Route', {
      origin: originIdentifier?.value,
      destination: destinationIdentifier?.value,
    });
  };

  const cardDescription = `${origin.name} to ${destination.name}`;

  const cardTitle =
    originIdentifier && destinationIdentifier
      ? `${originIdentifier?.value} → ${destinationIdentifier?.value}`
      : cardDescription;

  const buttonUrl = showScheduleLink
    ? createScheduleSearchPageUrl({ origin, destination, mode: routeSummary.routeType, dateRange })
    : undefined;

  const { base, header, grid, locationText, summaryTitle, summaryText, heading, schedulesButtonLink } = styles({
    layout,
  });

  return (
    <div className={base()}>
      <div className={grid()}>
        <div className={header()}>
          <ModeIcon mode={routeSummary.routeType} size="sm" />
          <Heading variant="h5" as="h3" className={heading()}>
            {customTitle || cardTitle}
          </Heading>
        </div>
        <Text className={locationText()}>{cardDescription}</Text>
      </div>
      <div className={grid()}>
        <Text className={summaryTitle()}>Duration / Frequency</Text>
        <Text className={summaryText()}>
          {formatDuration(routeSummary.durationSeconds)}
          {routeSummary.frequency ? `, ${formatFrequency(routeSummary.frequency)}` : ``}
        </Text>
      </div>
      {routeSummary.emissions && (
        <div className={grid()}>
          <Text className={summaryTitle()}>Emissions</Text>
          <EmissionsBadge emissionStats={routeSummary.emissions} />
        </div>
      )}
      {buttonUrl &&
        (layout === 'row' ? (
          <Button href={buttonUrl} variant="primaryLight" size="small" iconLeft="search" onClick={handleSearch}>
            Search
          </Button>
        ) : (
          <Button href={buttonUrl} variant="link" className={schedulesButtonLink()} onClick={handleSeeAlternative}>
            See schedules <ExternalLink size={12} />
          </Button>
        ))}
    </div>
  );
};

const styles = tv({
  slots: {
    base: 'border-b border-dashed border-grey-200 p-4 text-baseSm leading-snug last:border-none',
    grid: 'mb-2',
    header: 'mb-1 flex items-center',
    heading: 'mb-0 ml-2',
    summaryTitle: 'hidden',
    locationText: 'hidden',
    summaryText: 'm-0 text-sm',
    schedulesButtonLink: 'mt-1 text-sm text-lightBlue-700 no-underline',
  },
  variants: {
    layout: {
      default: {},
      row: {
        header: 'm-0',
        grid: 'm-0 grid grid-rows-[20px_1fr] items-center gap-1',
        base: 'flex min-w-[700px] flex-row items-center justify-between gap-4',
        summaryTitle: 'mb-1 block text-sm text-text-secondary',
        locationText: 'm-0 block max-w-36 text-sm italic',
      },
    },
  },
});

export { SchedulesAlternativeRouteCard };
